import {
  errorMap,
  Form,
  Main,
  Padding,
  Stack,
  TotalEntries,
  useDirectory,
  ValidPeriodOptions,
  DataObjectVersionFilters,
  DataObjectDescriptionFilter,
  EMPTY_PAGE,
  useValidationDashboardsQuery,
  omit,
  DashboardType,
  VISUAL_VALIDATION_FILTER_KEYS,
  CustomPagination,
  useGenerateVisualValidationComparisonMutation,
  zIsoDate,
  useDataObjectGroupsWithDescriptionsQuery,
  DataObjectGroupType,
  useGroupDescriptionOptions,
  useDataObjectVersionsQuery,
  useDuplicateVisualValidationComparisonMutation,
  VisualValidationFilters,
  type ISODate,
  useChangeVisualValidationComparisonDataMutation,
  useVisualValidationDashboardActions,
  ValidationDashboardListDto,
  InfoIconUrl,
} from '@atlas-frontend/sdk';
import styled from 'styled-components';
import { path } from '../../../paths';

import translations from './Comparison.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import z from 'zod/v3';
import { Flex } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { VisualValidationHeader } from '../VisualValidationHeader';
import { Divider } from 'antd';
import { VisualValidationActionButtons } from '../VisualValidationActionButtons';
import { keepPreviousData } from '@tanstack/react-query';
import { ValidationDashboardsTable } from '../ValidationDashboardsTable';
import { useFormContext, useWatch } from 'react-hook-form';
import { CommentsModal, hasDescriptionId } from '../modals/CommentsModal';
import {
  AlgorithmsModal,
  ModalData,
  SourcesModal,
} from '../modals/DetailsModal';

const { addTranslations, t } = i18nNsFactory('visual-validation-comparison');
addTranslations(translations);

type ComparisonFilters = VisualValidationFilters & {
  comparisonValueDate1?: string;
  comparisonVersion1?: string;
  comparisonValueDate2?: string;
  comparisonVersion2?: string;
};

const FILTER_KEYS: (keyof ComparisonFilters)[] = [
  ...VISUAL_VALIDATION_FILTER_KEYS,
  'comparisonValueDate1',
  'comparisonVersion1',
  'comparisonValueDate2',
  'comparisonVersion2',
];

export const ComparisonFormSchema = z.object({
  groupDescriptionRef: z
    .string(errorMap(() => t('validation.groupDescriptionRef.required')))
    .min(1),
  comparisonValueDate1: zIsoDate(() =>
    t('validation.comparisonValueDate1.required'),
  ),
  comparisonVersion1: z
    .number(errorMap(() => t('validation.comparisonVersion1.required')))
    .min(1),
  comparisonValueDate2: zIsoDate(() =>
    t('validation.comparisonValueDate2.required'),
  ),
  comparisonVersion2: z
    .number(errorMap(() => t('validation.comparisonVersion2.required')))
    .min(1),
});
type ComparisonFormType = z.infer<typeof ComparisonFormSchema>;

export const Comparison = () => (
  <Form schema={ComparisonFormSchema} blockNavigationOnFirstChange={false}>
    <Main>
      <ComparisonInner />
    </Main>
  </Form>
);

const ComparisonInner = () => {
  const directoryProps = useDirectory({
    queryFilterKeys: FILTER_KEYS,
  });
  const {
    queryParameters,
    setQueryFilters,
    tableProps,
    headerProps,
    setTableFilters,
    paginationProps,
  } = directoryProps;
  const { handleSubmit, getValues, trigger } =
    useFormContext<ComparisonFormType>();

  const {
    descriptionOptions,
    selectedDescription,
    isFetchingDescriptions,
    refetchDescriptions,
    versions1,
    isFetchingVersions1,
    selectedVersion1,
    versions2,
    isFetchingVersions2,
    selectedVersion2,
    comparisonValueDate1,
    comparisonValueDate2,
  } = useComparisonSelection(queryParameters, setQueryFilters);
  const descriptionId = selectedDescription?.descriptionId;

  const [periodOptions, setPeriodOptions] = useState<ValidPeriodOptions>({});
  const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false);
  const [algorithmsModalData, setAlgorithmsModalData] =
    useState<ModalData | null>(null);
  const [sourcesModalData, setSourcesModalData] = useState<ModalData | null>(
    null,
  );

  const {
    data: { content, totalElements } = EMPTY_PAGE,
    isFetching,
    refetch,
  } = useValidationDashboardsQuery(
    {
      ...omit(queryParameters, ['groupDescriptionRef']),
      dataObjectDescriptionId: descriptionId!,
      dashboardType: DashboardType.COMPARISON,
    },
    {
      placeholderData: keepPreviousData,
      enabled: !!descriptionId,
    },
  );

  const getRequestData = useCallback(() => {
    const [dataObjectVersion1Id, dataObjectVersion2Id] = getValues([
      'comparisonVersion1',
      'comparisonVersion2',
    ]);
    return {
      dataObjectVersion1Id,
      dataObjectVersion2Id,
    };
  }, [getValues]);

  const openDetailsModal = useCallback(
    (
      modalType: 'algorithms' | 'sources',
      rowData?: ValidationDashboardListDto,
    ) => {
      if (!selectedDescription) return;
      if (rowData && rowData.versions.length < 2) return;
      const version = rowData
        ? rowData.versions[0].dataObjectVersion
        : selectedVersion1;
      const versionOther = rowData
        ? rowData.versions[1].dataObjectVersion
        : selectedVersion2;
      if (!version || !versionOther) return;
      const data = {
        type: DashboardType.COMPARISON,
        version,
        versionOther,
        descriptionOption: selectedDescription,
      } as const;
      if (modalType === 'algorithms') setAlgorithmsModalData(data);
      else setSourcesModalData(data);
    },
    [selectedDescription, selectedVersion1, selectedVersion2],
  );

  const {
    createDashboard,
    openDashboard,
    duplicateDashboard,
    changeDashboardData,
  } = useVisualValidationDashboardActions<{
    dataObjectVersion1Id: number;
    dataObjectVersion2Id: number;
  }>({
    hasAccess: selectedDescription?.hasAccess || false,
    popupWindowPath: path.visualValidationGenerating,
    getRequestData,
    useCreate: useGenerateVisualValidationComparisonMutation,
    useDuplicate: useDuplicateVisualValidationComparisonMutation,
    useChangeData: useChangeVisualValidationComparisonDataMutation,
    onValidationFailed: trigger,
    onSuccess: refetch,
  });

  return (
    <Main.Col>
      <Main.Header />
      <Main.Content padding={false} id="visual-validation-comparison">
        {isCommentsModalOpen && selectedDescription && (
          <CommentsModal
            descriptionOption={selectedDescription}
            type="compare"
            valueDates={[
              comparisonValueDate1 || null,
              comparisonValueDate2 || null,
            ]}
            refetchDescriptions={refetchDescriptions}
            onClose={() => setIsCommentsModalOpen(false)}
          />
        )}
        {algorithmsModalData && (
          <AlgorithmsModal
            {...algorithmsModalData}
            onClose={() => setAlgorithmsModalData(null)}
          />
        )}
        {sourcesModalData && (
          <SourcesModal
            {...sourcesModalData}
            onClose={() => setSourcesModalData(null)}
          />
        )}
        <ContentWrapper>
          <VisualValidationHeader
            infoIconUrl={InfoIconUrl.VISUAL_VALIDATION_COMPARISON}
          />

          <Padding horizontal>
            <DataObjectDescriptionFilter
              options={descriptionOptions}
              isLoading={isFetchingDescriptions}
              onChange={() => setPeriodOptions({})}
            />
          </Padding>
          <Divider />
          <Padding horizontal bottom>
            <Flex gap="small" wrap="wrap" justify="space-between">
              <Flex gap="small" wrap="wrap">
                <DataObjectVersionFilters
                  versions={versions1 || []}
                  isLoading={isFetchingVersions1}
                  periodOptions={periodOptions}
                  setPeriodOptions={setPeriodOptions}
                  valueDateFieldName="comparisonValueDate1"
                  versionFieldName="comparisonVersion1"
                />
                <DataObjectVersionFilters
                  versions={versions2 || []}
                  isLoading={isFetchingVersions2}
                  periodOptions={periodOptions}
                  setPeriodOptions={setPeriodOptions}
                  valueDateFieldName="comparisonValueDate2"
                  versionFieldName="comparisonVersion2"
                />
              </Flex>
              <VisualValidationActionButtons
                hasDescription={!!descriptionId}
                hasVersions={!!selectedVersion1 && !!selectedVersion2}
                hasComments={selectedDescription?.hasComments || false}
                onCommentsClick={() => {
                  setIsCommentsModalOpen(true);
                }}
                onAlgorithmsClick={() => openDetailsModal('algorithms')}
                onSourcesClick={() => openDetailsModal('sources')}
                onNewDashboardClick={() => handleSubmit(createDashboard)()}
                filtersCount={headerProps.filtersCount}
                onFilterClear={headerProps.onFilterClear}
              />
            </Flex>
          </Padding>
          <Padding horizontal bottom>
            <Flex vertical gap="small">
              <ValidationDashboardsTable
                data={content || []}
                isLoading={isFetching}
                setFilters={setTableFilters}
                scrollContainerSelector="#visual-validation-comparison"
                refetchData={refetch}
                onClickOpen={openDashboard}
                onClickDuplicate={duplicateDashboard}
                onClickChangeData={changeDashboardData}
                onClickSourceData={(d) => openDetailsModal('sources', d)}
                onClickAlgorithmData={(d) => openDetailsModal('algorithms', d)}
                {...tableProps}
              />
            </Flex>
          </Padding>
        </ContentWrapper>
      </Main.Content>
      <Main.Footer>
        <TotalEntries count={totalElements} />
        <FooterPagination {...paginationProps} />
      </Main.Footer>
    </Main.Col>
  );
};

const useComparisonSelection = (
  queryParameters: Partial<ComparisonFilters>,
  setQueryFilters: (filters: Partial<ComparisonFilters>) => void,
) => {
  const {
    groupDescriptionRef,
    comparisonValueDate1,
    comparisonValueDate2,
    comparisonVersion1,
    comparisonVersion2,
  } = useWatch<ComparisonFormType>();
  const { reset } = useFormContext<ComparisonFormType>();

  // Update query parameters when form values change
  useEffect(() => {
    setQueryFilters({
      groupDescriptionRef,
      comparisonValueDate1: comparisonValueDate1,
      comparisonVersion1: comparisonVersion1
        ? String(comparisonVersion1)
        : undefined,
      comparisonValueDate2: comparisonValueDate2,
      comparisonVersion2: comparisonVersion2
        ? String(comparisonVersion2)
        : undefined,
    });
  }, [
    groupDescriptionRef,
    comparisonValueDate1,
    comparisonVersion1,
    comparisonValueDate2,
    comparisonVersion2,
    setQueryFilters,
  ]);

  // Reset form values when query parameters change
  useEffect(() => {
    const comparisonValueDate1Param = queryParameters?.comparisonValueDate1 as
      | ISODate
      | undefined;
    const comparisonVersion1Param = queryParameters?.comparisonVersion1;
    const comparisonValueDate2Param = queryParameters?.comparisonValueDate2 as
      | ISODate
      | undefined;
    const comparisonVersion2Param = queryParameters?.comparisonVersion2;

    reset({
      ...queryParameters,
      comparisonValueDate1: comparisonValueDate1Param,
      comparisonVersion1: comparisonVersion1Param
        ? Number(comparisonVersion1Param)
        : undefined,
      comparisonValueDate2: comparisonValueDate2Param,
      comparisonVersion2: comparisonVersion2Param
        ? Number(comparisonVersion2Param)
        : undefined,
    });
  }, [queryParameters, reset]);

  const {
    data: dataObjectGroupsWithDescriptions,
    refetch: refetchDescriptions,
    isFetching: isFetchingDescriptions,
  } = useDataObjectGroupsWithDescriptionsQuery(
    {
      groupType: DataObjectGroupType.O,
    },
    {
      placeholderData: keepPreviousData,
    },
  );
  const descriptionOptions = useGroupDescriptionOptions(
    dataObjectGroupsWithDescriptions,
    false,
  );

  const selectedDescription = useMemo(
    () =>
      descriptionOptions
        .flatMap((options) => options)
        .filter(hasDescriptionId)
        .find((option) => option.value === groupDescriptionRef),
    [descriptionOptions, groupDescriptionRef],
  );
  const descriptionId = selectedDescription?.descriptionId;

  const { data: versions1 = [], isFetching: isFetchingVersions1 } =
    useDataObjectVersionsQuery(
      {
        id: descriptionId!,
        valueDate: comparisonValueDate1,
      },
      {
        enabled: !!descriptionId && !!comparisonValueDate1,
        placeholderData: keepPreviousData,
      },
    );
  const selectedVersion1 = useMemo(
    () => versions1?.find((ver) => ver.id === comparisonVersion1),
    [versions1, comparisonVersion1],
  );

  const { data: versions2 = [], isFetching: isFetchingVersions2 } =
    useDataObjectVersionsQuery(
      {
        id: descriptionId!,
        valueDate: comparisonValueDate2,
      },
      {
        enabled: !!descriptionId && !!comparisonValueDate2,
        placeholderData: keepPreviousData,
      },
    );
  const selectedVersion2 = useMemo(
    () => versions2?.find((ver) => ver.id === comparisonVersion2),
    [versions2, comparisonVersion2],
  );

  return {
    descriptionOptions,
    selectedDescription,
    isFetchingDescriptions,
    refetchDescriptions,
    versions1,
    isFetchingVersions1,
    selectedVersion1,
    versions2,
    isFetchingVersions2,
    selectedVersion2,
    comparisonValueDate1,
    comparisonValueDate2,
  };
};

const FooterPagination = styled(CustomPagination)`
  margin-left: auto;
`;

const ContentWrapper = styled(Stack)`
  width: 100%;
  max-width: 120em;
  height: 100%;
  margin-right: auto;
  margin-left: auto;
`;
